/**
 * `StatusBullet` — the single status-colour language for the Agents pane.
 *
 * Operator direction 2026-07-20: a small coloured bullet says as much as a
 * ringed check icon, more quietly — green done, amber processing, red failed.
 * Defined once so mission rows, harness rows, scheduled runs and their steps
 * cannot drift into four slightly-different palettes.
 *
 * The colour carries the meaning; the shape stays a plain dot. Only two
 * states break that rule, and deliberately:
 *   - `processing` PULSES — motion is what distinguishes "working" from a
 *     static state at a glance, without the loud blue spinner it replaces;
 *   - `awaiting` keeps a hollow ring, because "waiting on YOU" is a
 *     different thing from "working" and must not read as mere progress.
 */
import { cn } from "@/lib/utils";

export type BulletStatus =
  | "processing"
  | "done"
  | "failed"
  | "awaiting"
  | "pending"
  | "canceled";

const DOT: Record<BulletStatus, string> = {
  // Amber, pulsing — the one in-flight state you want your eye to catch.
  processing: "bg-amber-500 animate-pulse",
  // Muted green — the common resting state; calm on purpose.
  done: "bg-emerald-600/60 dark:bg-emerald-500/60",
  failed: "bg-red-500",
  // Rendered as a ring below, not a filled dot.
  awaiting: "border border-amber-500",
  pending: "bg-slate-300 dark:bg-slate-600",
  canceled: "bg-slate-400/60 dark:bg-slate-500/60",
};

export function StatusBullet({
  status,
  size = 7,
  className,
}: {
  status: BulletStatus;
  /** Diameter in px. Steps use a slightly smaller dot than run headers. */
  size?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn("mt-1 shrink-0 rounded-full", DOT[status], className)}
      style={{ width: size, height: size }}
    />
  );
}
